import React, { useMemo, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const TEXT_FIELDS = ['preAppNo', 'islemYapan', 'bayiAdi', 'bayiKodu', 'gunFarki'];
const DATE_FIELDS = ['islemTarihi'];
const DROPDOWN_FIELDS = ['islem'];

const getIslemTranslationKey = (islem) => {
    if (!islem) return null;

    switch (islem) {
        case 'Taslak':
            return 'preApprovalHistoryReport.values.taslak';
        case 'ilk talep':
            return 'preApprovalHistoryReport.values.ilkTalep';
        case 'Return to SERVICE':
            return 'preApprovalHistoryReport.values.returnToService';
        case 'Onaylandi':
            return 'preApprovalHistoryReport.values.onaylandi';
        case 'Reddedildi':
            return 'preApprovalHistoryReport.values.reddedildi';
        default:
            return null;
    }
};

const PreAppHistoryReportTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();

    const translateIslem = useCallback(
        (islem) => {
            const key = getIslemTranslationKey(islem);
            return key ? th(key) : islem || '-';
        },
        [th]
    );
    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const islemOptions = useMemo(
        () => [
            { label: th('preApprovalHistoryReport.values.taslak'), value: 'Taslak' },
            { label: th('preApprovalHistoryReport.values.ilkTalep'), value: 'ilk talep' },
            { label: th('preApprovalHistoryReport.values.returnToService'), value: 'Return to SERVICE' },
            { label: th('preApprovalHistoryReport.values.onaylandi'), value: 'Onaylandi' },
            { label: th('preApprovalHistoryReport.values.reddedildi'), value: 'Reddedildi' }
        ],
        [th]
    );

    const islemFilterTemplate = (options) => <Dropdown value={options.value} options={islemOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.select')} className="p-column-filter" showClear />;

    const tableFilters = useMemo(
        () => ({
            preAppNo: { value: activeFilters?.preAppNo ?? null, matchMode: activeFilters?.preAppNoMatchMode ?? FilterMatchMode.CONTAINS },
            islem: { value: activeFilters?.islem ?? null, matchMode: activeFilters?.islemMatchMode ?? FilterMatchMode.CONTAINS },
            islemYapan: { value: activeFilters?.islemYapan ?? null, matchMode: activeFilters?.islemYapanMatchMode ?? FilterMatchMode.CONTAINS },
            bayiAdi: { value: activeFilters?.bayiAdi ?? null, matchMode: activeFilters?.bayiAdiMatchMode ?? FilterMatchMode.CONTAINS },
            bayiKodu: { value: activeFilters?.bayiKodu ?? null, matchMode: activeFilters?.bayiKoduMatchMode ?? FilterMatchMode.CONTAINS },
            islemTarihi: { value: activeFilters?.islemTarihi ?? null, matchMode: activeFilters?.islemTarihiMatchMode ?? FilterMatchMode.DATE_IS },
            gunFarki: { value: activeFilters?.gunFarki ?? null, matchMode: activeFilters?.gunFarkiMatchMode ?? FilterMatchMode.EQUALS }
        }),
        [activeFilters]
    );

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            DATE_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.DATE_IS;
                }
            });

            DROPDOWN_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = FilterMatchMode.EQUALS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            let d = e.value;
            if (d instanceof Date) d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            options.filterApplyCallback(d);
        };
        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon showButtonBar className="p-column-filter" style={{ minWidth: '12rem' }} />;
    };

    const createTextColumn = (field, header, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header,
            style: { minWidth },
            body,
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const tableItems = (items ?? []).map((item, index) => ({
        ...item,
        rowKey: `${item.preAppNo}-${item.islemTarihi}-${index}`
    }));

    const columns = [
        createTextColumn('preAppNo', th('preApprovalHistoryReport.columns.preAppNo'), { minWidth: '140px' }),
        {
            field: 'islem',
            columnKey: 'islem',
            header: th('preApprovalHistoryReport.columns.islem'),
            sortable: true,
            style: { minWidth: '160px' },
            body: (row) => translateIslem(row.islem),
            filter: true,
            filterField: 'islem',
            filterElement: islemFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        },
        createTextColumn('islemYapan', th('preApprovalHistoryReport.columns.islemYapan'), { minWidth: '180px' }),
        {
            field: 'islemTarihi',
            columnKey: 'islemTarihi',
            header: th('preApprovalHistoryReport.columns.islemTarihi'),
            sortable: true,
            style: { minWidth: '180px' },
            body: (row) => formatDateOnly(row.islemTarihi),
            filter: true,
            filterField: 'islemTarihi',
            filterElement: dateFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        },
        createTextColumn('bayiAdi', th('preApprovalHistoryReport.columns.bayiAdi'), { minWidth: '200px' }),
        createTextColumn('bayiKodu', th('preApprovalHistoryReport.columns.bayiKodu'), { minWidth: '120px' }),
        createTextColumn('gunFarki', th('preApprovalHistoryReport.columns.gunFarki'), {
            minWidth: '110px',
            body: (row) =>
                row.gunFarki != null ? (
                    <span className="font-semibold">
                        {row.gunFarki} {th('preApprovalHistoryReport.days')}
                    </span>
                ) : (
                    <span className="text-color-secondary">-</span>
                )
        })
    ];

    return (
        <StickyScrollTable deps={[tableItems, activeFilters]} scrollBarPosition="top">
            <PreferencesDataTable
                tableKey="pre-approval-history-report"
                columns={columns}
                value={tableItems}
                totalRecords={totalCount}
                className="p-datatable-gridlines"
                showGridlines
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows || 20}
                onPage={onPage}
                dataKey="rowKey"
                loading={loading}
                filterDisplay="row"
                filters={tableFilters}
                onFilter={handleTableFilter}
                emptyMessage={th('common.noRecords')}
                rowsPerPageOptions={[10, 20, 50, 100]}
                tableStyle={{ minWidth: '80rem' }}
                onSort={(e) => onSortChange?.(e)}
                sortField={sortField}
                sortOrder={sortOrder}
                sortMode="single"
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
        </StickyScrollTable>
    );
};

export default PreAppHistoryReportTable;
